import axios from 'axios';
import { api } from "api/base";

export const getDeliveries = async () => {
    const response = await api.get('/deliveries');
    if (response.status === 200) {
        return response.data;
    } else {
        return null;
    }
};

export const createDeliveryGroup = async (orderIds: string[], routeId: string) => {
    const response = await api.post('/deliveries', { order_ids:orderIds, route_id: routeId });
    if (response.status === 200) {
        response.data.created_at = convertToLocalTime(response.data.created_at)
        response.data.updated_at = convertToLocalTime(response.data.updated_at)
        return response.data;
    } else {
        return null;
    }
};


export interface FileRef {
    s3_key: string;
    filename: string;
    content_type: string;
    file_type?: string;
}

export const getPresignedUrls = async (deliveryId: string, files: { filename: string; content_type: string }[]) => {
    const response = await api.post(`/deliveries/${deliveryId}/presigned-urls`, files);
    if (response.status === 200) {
        return response.data as { presigned_url: string; s3_key: string; filename: string; content_type: string }[];
    }
    return null;
};

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const uploadFileToS3 = async (
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (pct: number) => void,
    onRetry?: (attempt: number) => void,
    maxRetries = 3,
) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            await axios.put(presignedUrl, file, {
                headers: { 'Content-Type': contentType },
                onUploadProgress: (e) => {
                    if (onProgress && e.total) {
                        onProgress(Math.round((e.loaded / e.total) * 100));
                    }
                },
            });
            return;
        } catch (err: any) {
            // Only retry on network-level failures (no response). HTTP errors (403, 5xx, etc.) are thrown immediately.
            if (attempt === maxRetries || err.response) throw err;
            onProgress?.(0);
            onRetry?.(attempt);
            await sleep(1000 * attempt); // 1s, then 2s
        }
    }
};

export const uploadFileViaBackend = async (
    deliveryId: string,
    file: File,
    onProgress?: (pct: number) => void
): Promise<FileRef> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const response = await api.post(`/deliveries/${deliveryId}/upload-file`, formData, {
        onUploadProgress: (e) => {
            if (onProgress && e.total) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        },
    });
    return response.data as FileRef;
};

export const completeDelivery = async (deliveryId: string, comment: string, files: FileRef[] = []) => {
    const response = await api.post(`/deliveries/${deliveryId}/complete`, { comment, files });
    if (response.status === 200) {
        response.data.created_at = convertToLocalTime(response.data.created_at)
        response.data.updated_at = convertToLocalTime(response.data.updated_at)
        return response.data;
    } else {
        return null;
    }
};

export const reopenDelivery = async (deliveryId: string) => {
    const response = await api.post(`/deliveries/${deliveryId}/reopen`);
    if (response.status === 200) {
        return response.data;
    } else {
        return null;
    }
};


const convertToLocalTime = (dateString: string) => {
    const date = new Date(dateString);
    const timeZoneOffset = new Date().getTimezoneOffset();
    const localDate = new Date(date.getTime() - timeZoneOffset * 60000);
    return localDate.toISOString();
}

export const reportDeliveryProblem = async (deliveryId: string, status: string, comment: string, comment_code_id: string, files: FileRef[] = []) => {
    const response = await api.post(`/deliveries/${deliveryId}/report-problem`, { status, comment, comment_code_id, files });
    if (response.status === 200) {
        response.data.created_at = convertToLocalTime(response.data.created_at)
        response.data.updated_at = convertToLocalTime(response.data.updated_at)
        return response.data;
    } else {
        return null;
    }
};


export const getCommentCodes = async () => {
    const response = await api.get('/deliveries/comment-codes');
    if (response.status === 200) {
        return response.data;
    } else {
        return null;
    }
};

export const addCommentToDelivery = async (deliveryId: string, comment: string, comment_code_id: string) => {
    const response = await api.post(`/deliveries/${deliveryId}/add-comment`, { comment, comment_code_id });
    if (response.status === 200) {
        return response.data;
    } else {
        return null;
    }
};


export const getDriverDeliveryGroups = async () => {
    const response = await api.get('/deliveries/driver/groups');
    if (response.status === 200) {
        return response.data;
    } else {
        return null;
    }
};

export const getAdminCompletedDeliveries = async () => {
    const response = await api.get('/deliveries/admin/completed');
    return response.status === 200 ? response.data : [];
};

export const getAdminCompletedGrouped = async () => {
    const response = await api.get('/deliveries/admin/completed/grouped');
    return response.status === 200 ? response.data : [];
};

export const toggleDeliveryReview = async (deliveryId: string) => {
    const response = await api.patch(`/deliveries/${deliveryId}/review`);
    return response.status === 200 ? response.data : null;
};

export const deleteDelivery = async (deliveryId: string) => {
    const response = await api.delete(`/deliveries/${deliveryId}`);
    return response.status === 200;
};

export const getDeliveryFileUrls = async (deliveryId: string): Promise<{ url: string; filename: string; content_type: string; file_type: string | null }[]> => {
    const response = await api.get(`/deliveries/${deliveryId}/file-urls`);
    return response.status === 200 ? response.data : [];
};

export interface ReportFilters {
    reviewed?: boolean | null;
    driverId?: string;
    clientSearch?: string;
    statusFilter?: string;
}

export const getDeliveryReport = async (dateFrom: string, dateTo: string, page: number, pageSize: number, filters: ReportFilters = {}) => {
    const params: any = { date_from: dateFrom, date_to: dateTo, page, page_size: pageSize };
    if (filters.reviewed != null) params.reviewed = filters.reviewed;
    if (filters.driverId) params.driver_id = filters.driverId;
    if (filters.clientSearch) params.client_search = filters.clientSearch;
    if (filters.statusFilter) params.status_filter = filters.statusFilter;
    const response = await api.get('/deliveries/admin/report', { params });
    return response.status === 200 ? response.data : null;
};

export const startZipExport = async (dateFrom: string, dateTo: string, filters: ReportFilters = {}): Promise<string | null> => {
    const params: any = { date_from: dateFrom, date_to: dateTo };
    if (filters.reviewed != null) params.reviewed = filters.reviewed;
    if (filters.driverId) params.driver_id = filters.driverId;
    if (filters.clientSearch) params.client_search = filters.clientSearch;
    if (filters.statusFilter) params.status_filter = filters.statusFilter;
    const response = await api.post('/deliveries/admin/report/zip', null, { params });
    return response.status === 200 ? response.data.job_id : null;
};

export const getZipStatus = async (jobId: string): Promise<{ status: string; url?: string; error?: string } | null> => {
    const response = await api.get(`/deliveries/admin/report/zip/${jobId}`);
    return response.status === 200 ? response.data : null;
};

export const downloadDeliveryReport = async (dateFrom: string, dateTo: string, filters: ReportFilters = {}) => {
    const params: any = { date_from: dateFrom, date_to: dateTo };
    if (filters.reviewed != null) params.reviewed = filters.reviewed;
    if (filters.driverId) params.driver_id = filters.driverId;
    if (filters.clientSearch) params.client_search = filters.clientSearch;
    if (filters.statusFilter) params.status_filter = filters.statusFilter;
    const response = await api.get('/deliveries/admin/report/export', {
        params,
        responseType: 'blob',
    });
    if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `entregas_${dateFrom}_${dateTo}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
};

